
import { supabase } from '../lib/supabase';
import { Category, Expense, Group, User, ExpenseStatus, Recurrence, TransactionType } from '../types';

// Helper to map DB snake_case to App camelCase
const mapExpenseFromDB = (data: any): Expense => ({
  id: data.id,
  description: data.description,
  amount: parseFloat(data.amount),
  date: data.date,
  categoryId: data.category_id,
  status: data.status as ExpenseStatus,
  recurrence: data.recurrence as Recurrence,
  paidByUserId: data.paid_by_user_id,
  groupId: data.group_id,
  splitEqually: data.split_equally,
  type: data.type as TransactionType || 'expense',
  receiptUrl: undefined,
  excludeDates: data.exclude_dates || [],
  isFavorite: data.is_favorite || false
});

const mapCategoryFromDB = (data: any): Category => ({
  id: data.id,
  name: data.name,
  icon: data.icon || 'Tag',
  color: data.color || '#94a3b8',
  budgetLimit: parseFloat(data.budget_limit) || 0,
  alertEnabled: data.alert_enabled ?? true
});

const mapUserFromDB = (data: any): User => ({
  id: data.id,
  name: data.name || 'Usuário',
  email: data.email || '',
  avatarUrl: data.avatar_url,
  monthlyBudget: parseFloat(data.monthly_budget) || 0,
  darkMode: data.dark_mode ?? false
});

export const storageService = {
  // --- EXPENSES ---
  getExpenses: async (): Promise<Expense[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapExpenseFromDB);
  },

  addExpense: async (expense: Partial<Expense>): Promise<Expense> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const dbPayload = {
      user_id: user.id,
      description: expense.description,
      amount: expense.amount,
      date: expense.date,
      category_id: expense.categoryId,
      group_id: expense.groupId,
      paid_by_user_id: expense.paidByUserId,
      status: expense.status,
      recurrence: expense.recurrence,
      split_equally: expense.splitEqually,
      type: expense.type,
      exclude_dates: []
    };

    const { data, error } = await supabase.from('expenses').insert(dbPayload).select().single();
    if (error) throw error;
    return mapExpenseFromDB(data);
  },

  updateExpense: async (expense: Partial<Expense>): Promise<void> => {
    if (!expense.id) return;

    const dbPayload = {
      description: expense.description,
      amount: expense.amount,
      date: expense.date,
      category_id: expense.categoryId,
      group_id: expense.groupId,
      paid_by_user_id: expense.paidByUserId,
      status: expense.status,
      recurrence: expense.recurrence,
      split_equally: expense.splitEqually,
      type: expense.type
    };

    const { error } = await supabase.from('expenses').update(dbPayload).eq('id', expense.id);
    if (error) throw error;
  },

  updateExpenseStatus: async (id: string, status: ExpenseStatus): Promise<void> => {
    const { error } = await supabase
      .from('expenses')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
  },

  toggleExpenseFavorite: async (id: string, isFavorite: boolean): Promise<void> => {
    const { error } = await supabase
      .from('expenses')
      .update({ is_favorite: isFavorite })
      .eq('id', id);

    if (error) throw error;
  },

  deleteExpense: async (id: string): Promise<void> => {
    const { error } = await supabase.from('expenses').delete().eq('id', id);
    if (error) throw error;
  },

  addExclusionDate: async (id: string, dateToExclude: string): Promise<void> => {
    // 1. Get current exclusions
    const { data: current, error: fetchError } = await supabase
      .from('expenses')
      .select('exclude_dates')
      .eq('id', id)
      .single();

    if (fetchError) throw fetchError;

    const newExclusions = [...(current.exclude_dates || []), dateToExclude];

    // 2. Update
    const { error } = await supabase
      .from('expenses')
      .update({ exclude_dates: newExclusions })
      .eq('id', id);

    if (error) throw error;
  },

  // --- CATEGORIES ---
  getCategories: async (): Promise<Category[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('user_id', user.id);

    if (error) throw error;
    return (data || []).map(mapCategoryFromDB);
  },

  saveCategories: async (categories: Category[]) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    for (const cat of categories) {
      if (cat.id.length < 10) {
        await supabase.from('categories').insert({
          user_id: user.id,
          name: cat.name,
          budget_limit: cat.budgetLimit,
          color: cat.color,
          icon: cat.icon,
          alert_enabled: cat.alertEnabled
        });
      } else {
        await supabase.from('categories').update({
          name: cat.name,
          budget_limit: cat.budgetLimit,
          color: cat.color,
          alert_enabled: cat.alertEnabled
        }).eq('id', cat.id);
      }
    }
  },

  addCategory: async (category: Partial<Category>): Promise<Category> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user');

    const { data, error } = await supabase.from('categories').insert({
      user_id: user.id,
      name: category.name,
      budget_limit: category.budgetLimit,
      color: category.color,
      icon: category.icon,
      alert_enabled: category.alertEnabled
    }).select().single();
    if (error) throw error;
    return mapCategoryFromDB(data);
  },

  updateCategory: async (category: Category): Promise<void> => {
    const { error } = await supabase.from('categories').update({
      name: category.name,
      budget_limit: category.budgetLimit,
      color: category.color,
      alert_enabled: category.alertEnabled
    }).eq('id', category.id);
    if (error) throw error;
  },

  deleteCategory: async (id: string): Promise<void> => {
    await supabase.from('categories').delete().eq('id', id);
  },

  // --- GROUPS ---
  getGroups: async (): Promise<Group[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Fetch groups where user is a member
    const { data: memberData, error } = await supabase
      .from('group_members')
      .select('group_id, groups(*)')
      .eq('user_id', user.id);

    if (error) throw error;

    const groups: Group[] = [];

    for (const item of (memberData || [])) {
      const groupInfo = item.groups as any;

      if (!groupInfo) continue;

      const { data: members, error: memError } = await supabase
        .from('group_members')
        .select('user_id, profiles(*)')
        .eq('group_id', groupInfo.id);

      if (!memError && members) {
        groups.push({
          id: groupInfo.id,
          name: groupInfo.name,
          description: groupInfo.description,
          members: (members || []).map((m: any) => m.profiles ? mapUserFromDB(m.profiles) : { id: m.user_id, name: 'Unknown User', email: '', monthlyBudget: 0 }),
          inviteCode: groupInfo.invite_code,
          createdBy: groupInfo.created_by
        });
      }
    }
    return groups;
  },

  saveGroups: async (groups: Group[]) => {
    // Handled via specific add/update/delete methods
  },

  addGroup: async (name: string, description: string, memberIds: string[]): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

    const { data: group, error } = await supabase.from('groups').insert({
      name, description, created_by: user.id, invite_code: inviteCode
    }).select().single();

    if (error) throw error;

    const uniqueMembers = Array.from(new Set([...memberIds, user.id]));

    const membersToAdd = uniqueMembers.map(uid => ({
      group_id: group.id,
      user_id: uid,
      is_admin: uid === user.id
    }));

    await supabase.from('group_members').insert(membersToAdd);
  },

  updateGroup: async (group: Group): Promise<void> => {
    await supabase.from('groups').update({
      name: group.name,
      description: group.description
    }).eq('id', group.id);
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await supabase.from('groups').delete().eq('id', groupId);
  },

  addMemberToGroup: async (groupId: string, userId: string): Promise<void> => {
    const { data: existing } = await supabase.from('group_members')
      .select('*')
      .eq('group_id', groupId)
      .eq('user_id', userId)
      .single();

    if (existing) return;

    const { error } = await supabase.from('group_members').insert({
      group_id: groupId,
      user_id: userId
    });

    if (error) throw error;
  },

  joinGroupByCode: async (code: string): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: group, error } = await supabase.from('groups').select('id').eq('invite_code', code.toUpperCase()).single();

    if (error || !group) return false;

    const { data: existing } = await supabase.from('group_members')
      .select('*')
      .eq('group_id', group.id)
      .eq('user_id', user.id)
      .single();

    if (existing) return true;

    await supabase.from('group_members').insert({
      group_id: group.id,
      user_id: user.id
    });

    return true;
  },

  // --- USER ---
  getCurrentUser: async (): Promise<User | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single();
    if (error || !data) return null;
    return mapUserFromDB(data);
  },

  getUserByEmail: async (email: string): Promise<User | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .ilike('email', email)
      .single();

    if (error || !data) return null;
    return mapUserFromDB(data);
  },

  saveUser: async (user: User) => {
    const { error } = await supabase.from('profiles').update({
      name: user.name,
      monthly_budget: user.monthlyBudget,
      avatar_url: user.avatarUrl,
      dark_mode: user.darkMode
    }).eq('id', user.id);
    if (error) throw error;
  },

  getAllUsers: async (): Promise<User[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // 1. Get current user profile
    const currentUserProfile = await storageService.getCurrentUser();

    // 2. Get groups (which includes members)
    const groups = await storageService.getGroups();

    // 3. Collect all unique members
    const uniqueUsersMap = new Map<string, User>();

    if (currentUserProfile) {
      uniqueUsersMap.set(currentUserProfile.id, currentUserProfile);
    }

    groups.forEach(group => {
      group.members.forEach(member => {
        uniqueUsersMap.set(member.id, member);
      });
    });

    return Array.from(uniqueUsersMap.values());
  }
};